import React, { useState, ReactNode } from "react";
import { useHistory } from "react-router-dom";
import { useCookies } from "react-cookie";

import AuthContext, { AuthenticatedUser } from "../../context/auth";
import LocalStorageService from "../../services/localStorage";
import {
  loginUser,
  userSignup,
  getRecentProducts,
  signOutUser,
  getMyInfo
} from "../../api/api";
import { client } from "../../api/users/client";
import useTrackUser from "../../analytics/hooks/useTrackUser";
import useTrackEvent from "../../analytics/hooks/useTrackEvent";

interface AuthProviderProps {
  children?: ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser>(
    LocalStorageService.getUser()
  );

  const [, , removeCookie] = useCookies();

  const trackUser = useTrackUser();
  const trackEvent = useTrackEvent();
  const history = useHistory<{ path?: string }>();

  const getUserInfo = async () => {
    try {
      const {
        data: { user }
      } = await getMyInfo();
      LocalStorageService.saveUser(user, trackUser);
      client.defaults.headers.common["X-API-Token"] = user.api_key;
      setAuthenticatedUser(user);
      trackEvent("Logged In", {
        submission_origin: "login",
        user_type: user.user_type,
        Path: "login",
        business_type:
          user.user_type === "business" && user.business_type_short_form
            ? user.business_type_short_form
            : undefined
      });
    } catch {
      history.push("/institutional-investors");
    }
  };

  const login = async (email: string, password: string) => {
    return loginUser(email, password).then(() => {
      history.push("/");
    });
  };

  const signup = async (info: any, redirectTo?: string) => {
    return userSignup(info).then(response => {
      const user = response.data;
      const { user: userData } = user;
      LocalStorageService.saveUser(userData, trackUser);
      client.defaults.headers.common["X-API-Token"] = user.api_key;
      setAuthenticatedUser(userData);
      history.push(redirectTo || "/");
    });
  };

  const logout = (
    config = {
      to: "/"
    }
  ) => {
    signOutUser().catch(() => removeCookie("_wikiposit_session_v8"));
    setAuthenticatedUser(null);
    LocalStorageService.removeUser();
    delete client.defaults.headers.common["X-API-Token"];
    history.push(config.to || "/");
  };

  const authenticated = () => {
    return !!authenticatedUser;
  };

  const getProducts = () => {
    return getRecentProducts().then(response => {
      client.defaults.headers.common["Access-Control-Allow-Origin"] = "*";
      client.defaults.headers.common["Access-Control-Allow-Methods"] =
        "GET, POST, PUT, DELETE";
      client.defaults.headers.common["Access-Control-Allow-Headers"] =
        "Authorization";

      return response;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        authenticatedUser,
        setAuthenticatedUser,
        getUserInfo,
        authenticated,
        login,
        signup,
        logout,
        getProducts
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
